import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { UserCheck, Plus, X, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerPartnerFunction = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('customer_partner_function');
  const [viewItem, setViewItem] = useState(null);
  const [partnerFunctions, setPartnerFunctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentPartnerFunction, setCurrentPartnerFunction] = useState(null);
  const [formData, setFormData] = useState({
    partner_function_id: '',
    name: '',
    partner_type: 'Customer',
    unique: false
  });

  useEffect(() => {
    loadPartnerFunctions();
  }, []);

  const loadPartnerFunctions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/customer-partner-functions`);
      setPartnerFunctions(response.data);
    } catch (error) {
      console.error('Error loading partner functions:', error);
      toast.error('Failed to load partner functions');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.partner_function_id || !formData.name) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      if (editMode) {
        await axios.put(
          `${BACKEND_URL}/api/customer-partner-functions/${currentPartnerFunction.partner_function_id}`,
          { 
            name: formData.name,
            partner_type: formData.partner_type,
            unique: formData.unique
          }
        );
        toast.success('Partner Function updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/customer-partner-functions`, formData);
        toast.success('Partner Function created successfully');
      }
      
      setShowDialog(false);
      resetForm();
      loadPartnerFunctions();
    } catch (error) {
      console.error('Error saving partner function:', error);
      toast.error(error.response?.data?.detail || 'Failed to save partner function');
    }
  };

  const handleEdit = (partnerFunction) => {
    setCurrentPartnerFunction(partnerFunction);
    setFormData({
      partner_function_id: partnerFunction.partner_function_id,
      name: partnerFunction.name,
      partner_type: partnerFunction.partner_type,
      unique: partnerFunction.unique
    });
    setEditMode(true);
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({ 
      partner_function_id: '', 
      name: '', 
      partner_type: 'Customer',
      unique: false
    });
    setCurrentPartnerFunction(null);
    setEditMode(false);
  };

  const handleDialogClose = () => {
    setShowDialog(false);
    resetForm();
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <UserCheck className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Customer Partner Functions</h1>
                <p className="text-sm text-gray-600">Define partner function types for customer relationships</p>
              </div>
            </div>
            {writable && (<Button
              onClick={() => setShowDialog(true)}
              className="bg-orange-600 hover:bg-orange-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Partner Function
            </Button>)}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-10 h-10 animate-spin text-orange-600 mb-3" />
              <p className="text-sm text-gray-600">Loading partner functions...</p>
            </div>
          ) : partnerFunctions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <UserCheck className="w-16 h-16 text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-1">No partner functions yet</h3>
              <p className="text-sm text-gray-600 mb-4">Create your first partner function to get started</p>
              {writable && (<Button onClick={() => setShowDialog(true)} className="bg-orange-600 hover:bg-orange-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Partner Function
              </Button>)}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Function ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Partner Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unique</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {partnerFunctions.map((pf) => (
                    <tr key={pf.partner_function_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-medium text-gray-900">{pf.partner_function_id}</span>
                      </td>
                      <td className="px-6 py-4">{pf.name}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded ${
                          pf.partner_type === 'Customer' 
                            ? 'bg-orange-100 text-orange-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {pf.partner_type}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {pf.unique ? (
                          <span className="px-2 py-1 bg-amber-100 text-amber-800 text-xs rounded">Yes</span>
                        ) : (
                          <span className="text-gray-400 text-sm">No</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <RecordActions doctype="customer_partner_function" onView={() => setViewItem(pf)} onEdit={() => handleEdit(pf)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Create/Edit Dialog */}
        {showDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editMode ? 'Edit Partner Function' : 'Add Partner Function'}
                </h2>
                <button
                  onClick={handleDialogClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Partner Function ID *</Label>
                  <Input
                    maxLength={10}
                    value={formData.partner_function_id}
                    onChange={(e) => setFormData({ ...formData, partner_function_id: e.target.value.toUpperCase() })}
                    placeholder="e.g., SP, BP, PY"
                    disabled={editMode}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Maximum 10 characters</p>
                </div>

                <div>
                  <Label>Name *</Label>
                  <Input
                    maxLength={100}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Sold-to Party, Bill-to Party"
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Maximum 100 characters</p>
                </div>

                <div>
                  <Label>Partner Type *</Label>
                  <select
                    value={formData.partner_type}
                    onChange={(e) => setFormData({ ...formData, partner_type: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="Customer">Customer</option>
                    <option value="Vendor">Vendor</option>
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="unique"
                    checked={formData.unique}
                    onChange={(e) => setFormData({ ...formData, unique: e.target.checked })}
                    className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                  />
                  <Label htmlFor="unique" className="cursor-pointer">
                    Unique (Only one partner allowed for this function)
                  </Label>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={handleDialogClose} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-orange-600 hover:bg-orange-700">
                    {editMode ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default CustomerPartnerFunction;
